import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  InputAdornment,
  Divider,
  Paper,
  Grid,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AccountBalance as VaultIcon,
  AttachMoney as SurchargeIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Star as StarIcon,
  Link as LinkIcon,
  SwapHoriz as SwapHorizIcon
} from '@mui/icons-material';
import { ApiContext } from '../contexts/ApiContext';

const PaiAccountMappings = () => {
  const { apiCall } = useContext(ApiContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Processor state
  const [processorType, setProcessorType] = useState('PAI');
  const [showProcessorInfo, setShowProcessorInfo] = useState(false);

  // Terminal state
  const [terminalId, setTerminalId] = useState('');
  const [locationName, setLocationName] = useState('');
  const [ghTerminalIds, setGhTerminalIds] = useState('');
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [isEditingGhTerminals, setIsEditingGhTerminals] = useState(false);
  const [terminalLoaded, setTerminalLoaded] = useState(false);
  
  // Vault account state
  const [vaultAccount, setVaultAccount] = useState(null);
  const [isEditingVault, setIsEditingVault] = useState(false);
  const [vaultForm, setVaultForm] = useState({
    nameOnAccount: '',
    route: '',
    account: '',
    defaultFee: '0.00',
    contractStatus: 'Active',
    settlementSchedule: 'Daily'
  });
  
  // Surcharge accounts state
  const [surchargeAccounts, setSurchargeAccounts] = useState([]);
  const [surchargeSettings, setSurchargeSettings] = useState({
    contractStatus: 'Active',
    settlementSchedule: 'Daily'
  });
  const [isAddingSurcharge, setIsAddingSurcharge] = useState(false);
  const [surchargeForm, setSurchargeForm] = useState({
    nameOnAccount: '',
    route: '',
    account: '',
    splitAmount: '',
    contractStatus: 'Active',
    settlementSchedule: 'Daily'
  });
  const [editingSurchargeId, setEditingSurchargeId] = useState(null);
  const [inlineEditSurcharge, setInlineEditSurcharge] = useState({}); // For inline editing
  const [editingInlineId, setEditingInlineId] = useState(null); // Track which surcharge is being edited inline
  
  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, account: null });

  // Delete Terminal dialog state
  const [deleteTerminalDialog, setDeleteTerminalDialog] = useState({ open: false, terminalId: '' });
  
  // Validation state
  const [validationErrors, setValidationErrors] = useState({});
  
  // Quick Add state
  const [frequentAccounts, setFrequentAccounts] = useState([]);
  const [existingSuggestions, setExistingSuggestions] = useState([]);

  // Related processor terminals state (all processors for same location)
  const [relatedProcessorTerminals, setRelatedProcessorTerminals] = useState([]);
  const [isAddingProcessorTerminal, setIsAddingProcessorTerminal] = useState(false);
  const [newProcessorTerminal, setNewProcessorTerminal] = useState({
    processorType: 'DNS',
    terminalNumber: ''
  });

  // Available locations state (for dropdown)
  const [availableLocations, setAvailableLocations] = useState([]);

  // Channel-hierarchy placement state: where this PAI location lives in
  // Org -> Channel -> Market. Free-solo (pick existing or type new).
  const [hierarchyOptions, setHierarchyOptions] = useState([]); // [{org, channel, market}]
  const [assignOrg, setAssignOrg] = useState('');
  const [assignChannel, setAssignChannel] = useState('');
  const [assignMarket, setAssignMarket] = useState('');

  // Load the distinct Org/Channel/Market tuples once for the cascading pickers.
  useEffect(() => {
    (async () => {
      try {
        const res = await apiCall('/api/v1/channels/stores?view=hierarchy-options', 'GET');
        setHierarchyOptions(res.options || []);
      } catch (e) {
        console.error('Failed to load hierarchy options:', e);
      }
    })();
  }, [apiCall]);

  // Fetch + prefill the current channel placement for a given location name.
  const loadPlacement = async (name) => {
    if (!name) { setAssignOrg(''); setAssignChannel(''); setAssignMarket(''); return; }
    try {
      const res = await apiCall(`/api/v1/channels/stores?view=hierarchy-options&store=${encodeURIComponent(name)}`, 'GET');
      if (res.options) setHierarchyOptions(res.options);
      const cur = res.current;
      setAssignOrg(cur?.org || '');
      setAssignChannel(cur?.channel || '');
      setAssignMarket(cur?.market || '');
    } catch (e) {
      console.error('Failed to load placement:', e);
    }
  };

  // Derived cascading option lists (free-solo, so these are suggestions only)
  const orgOptionList = [...new Set(hierarchyOptions.map(o => o.org).filter(Boolean))].sort();
  const channelOptionList = [...new Set(hierarchyOptions
    .filter(o => !assignOrg || o.org === assignOrg)
    .map(o => o.channel).filter(Boolean))].sort();
  const marketOptionList = [...new Set(hierarchyOptions
    .filter(o => (!assignOrg || o.org === assignOrg) && (!assignChannel || o.channel === assignChannel))
    .map(o => o.market).filter(Boolean))].sort();

  // Calculate split counter totals
  const calculateSplitTotals = () => {
    const vaultFee = parseFloat(vaultAccount?.default_fee || vaultForm.defaultFee || 0);
    const totalSplit = surchargeAccounts.reduce((sum, acc) => {
      // Handle both snake_case from API and camelCase from form
      const splitAmount = acc.split_amount || acc.splitAmount || 0;
      return sum + parseFloat(splitAmount);
    }, 0);
    return { vaultFee, totalSplit };
  };

  const saveAllChanges = async () => {
    if (!locationName.trim()) {
      setError('Location name is required');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const savePromises = [];
      
      // If no accounts exist at all, create a placeholder vault account to store location
      if (!vaultAccount && surchargeAccounts.length === 0) {
        const placeholderPayload = {
          terminalNumber: terminalId,
          locationName: locationName,
          ghTerminalIds: ghTerminalIds,
          nameOnAccount: 'PLACEHOLDER',
          route: '000000000',
          account: '00000',
          accountType: 'Vault',
          defaultFee: '0.00',
          contractStatus: 'Inactive',
          settlementSchedule: 'Daily',
          splitAmount: null,
          processorType: processorType
        };
        
        savePromises.push(
          apiCall('/api/v1/pai/account-mappings', 'POST', placeholderPayload)
        );
      }
      // Save or update vault account
      else if (isEditingVault && vaultForm.nameOnAccount) {
        const vaultPayload = {
          terminalNumber: terminalId,
          locationName: locationName,
          ghTerminalIds: ghTerminalIds,
          ...vaultForm,
          accountType: 'Vault',
          splitAmount: null,
          processorType: processorType
        };
        
        if (vaultAccount) {
          savePromises.push(
            apiCall(`/api/v1/pai/account-mappings/${vaultAccount.id}`, 'PUT', vaultPayload)
          );
        } else {
          savePromises.push(
            apiCall('/api/v1/pai/account-mappings', 'POST', vaultPayload)
          );
        }
      } else if (vaultAccount) {
        // Update location name for existing vault (always update, even if unchanged)
        savePromises.push(
          apiCall(`/api/v1/pai/account-mappings/${vaultAccount.id}`, 'PUT', {
            terminalNumber: vaultAccount.terminal_number,
            nameOnAccount: vaultAccount.name_on_account,
            locationName: locationName,
            ghTerminalIds: ghTerminalIds,
            route: vaultAccount.route,
            account: vaultAccount.account,
            accountType: 'Vault',
            defaultFee: vaultAccount.default_fee,
            contractStatus: vaultAccount.contract_status,
            settlementSchedule: vaultAccount.settlement_schedule,
            splitAmount: null,
            processorType: processorType
          })
        );
      }
      
      // Update location name for all surcharge accounts
      for (const surcharge of surchargeAccounts) {
        savePromises.push(
          apiCall(`/api/v1/pai/account-mappings/${surcharge.id}`, 'PUT', {
            terminalNumber: surcharge.terminal_number,
            nameOnAccount: surcharge.name_on_account,
            locationName: locationName,
            route: surcharge.route,
            account: surcharge.account,
            accountType: 'Surcharge',
            splitAmount: surcharge.split_amount,
            defaultFee: surcharge.default_fee,
            contractStatus: surcharge.contract_status,
            settlementSchedule: surcharge.settlement_schedule,
            processorType: processorType
          })
        );
      }
      
      if (savePromises.length > 0) {
        await Promise.all(savePromises);
      }

      // Place the location into an existing Org -> Channel -> Market slot.
      // Requires all three (selectable from existing values only). Leaving them
      // blank keeps any current placement intact. This is the core fix: a PAI
      // location born here is now registered in the hierarchy and channel-assigned.
      const hasPlacement = locationName && assignOrg && assignChannel && assignMarket;
      if (hasPlacement) {
        try {
          await apiCall('/api/v1/channels/stores', 'POST', {
            action: 'place-location',
            store: locationName,
            org: assignOrg,
            channel: assignChannel,
            market: assignMarket
          });
        } catch (e) {
          console.error('Channel placement failed:', e);
          setError('Saved accounts, but channel placement failed: ' + (e.message || 'unknown error'));
        }
      }

      if (savePromises.length > 0) {
        setSuccess('All changes saved successfully');
        setIsEditingLocation(false);
        setIsEditingVault(false);
        // Reload to get updated data, preserving the location name we just saved
        await loadTerminal(true);
      } else if (hasPlacement) {
        setSuccess('Channel placement saved');
      } else {
        setSuccess('No changes to save');
      }
    } catch (err) {
      setError('Failed to save changes');
      console.error('Error saving changes:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTerminal = async (preserveLocationName = false) => {
    if (!terminalId.trim()) {
      setError('Please enter a terminal ID');
      return;
    }

    setLoading(true);
    setError(null);
    // Don't reset location name here - we'll set it based on the fetched data

    try {
      // First try to load without processor filter - this allows loading any terminal ID
      let response = await apiCall(`/api/v1/pai/account-mappings?terminal=${terminalId}`);
      let accounts = response.accounts || [];

      // If no results, the terminal doesn't exist yet - that's okay, we'll create it
      // Auto-detect processor type from the results if we found accounts
      if (accounts.length > 0) {
        const detectedProcessor = accounts[0].processor_type || processorType;
        if (detectedProcessor !== processorType) {
          setProcessorType(detectedProcessor);
        }
      }
      
      // Separate vault and surcharge accounts
      const vaultAcc = accounts.find(acc => acc.account_type === 'Vault');
      const surchargeAccs = accounts.filter(acc => acc.account_type === 'Surcharge');
      
      setVaultAccount(vaultAcc || null);
      setSurchargeAccounts(surchargeAccs);
      
      // Set location name and GH terminals if any account has them (or preserve the current ones if requested)
      if (!preserveLocationName) {
        // Check if any account has a location name
        const hasLocationName = (vaultAcc && vaultAcc.location_name) || 
                               (surchargeAccs.length > 0 && surchargeAccs[0].location_name);
        
        // Load GH terminal IDs
        const ghTerminals = (vaultAcc && vaultAcc.gh_terminal_ids) || 
                          (surchargeAccs.length > 0 && surchargeAccs[0].gh_terminal_ids) || '';
        
        if (vaultAcc && vaultAcc.location_name) {
          setLocationName(vaultAcc.location_name);
          setIsEditingLocation(false); // Location exists, start as read-only
        } else if (surchargeAccs.length > 0 && surchargeAccs[0].location_name) {
          setLocationName(surchargeAccs[0].location_name);
          setIsEditingLocation(false); // Location exists, start as read-only
        } else {
          // No location name found - always allow editing
          setLocationName('');
          setIsEditingLocation(true); // Allow editing since no location exists
        }
        
        // Set GH terminal IDs
        setGhTerminalIds(ghTerminals);
        setIsEditingGhTerminals(ghTerminals ? false : true); // Read-only if exists, editable if not
      } else {
        // When preserving location name after save, check if it's now saved
        if ((vaultAcc && vaultAcc.location_name) || (surchargeAccs.length > 0 && surchargeAccs[0].location_name)) {
          setIsEditingLocation(false); // Location now exists, switch to read-only
        }
        if ((vaultAcc && vaultAcc.gh_terminal_ids) || (surchargeAccs.length > 0 && surchargeAccs[0].gh_terminal_ids)) {
          setIsEditingGhTerminals(false); // GH terminals now exist, switch to read-only
        }
      }
      
      // Set vault form if vault account exists
      if (vaultAcc) {
        setVaultForm({
          nameOnAccount: vaultAcc.name_on_account,
          route: vaultAcc.route,
          account: vaultAcc.account,
          defaultFee: vaultAcc.default_fee,
          contractStatus: vaultAcc.contract_status || 'Active',
          settlementSchedule: vaultAcc.settlement_schedule || 'Daily'
        });
      }
      
      // Set shared surcharge settings from first surcharge account if available
      if (surchargeAccs.length > 0) {
        setSurchargeSettings({
          contractStatus: surchargeAccs[0].contract_status || 'Active',
          settlementSchedule: surchargeAccs[0].settlement_schedule || 'Daily'
        });
      }
      
      setTerminalLoaded(true);
      setSuccess(`Terminal ${terminalId} loaded successfully`);

      // Prefill the channel placement for this location (if it's already placed).
      const resolvedName = (vaultAcc && vaultAcc.location_name)
        || (surchargeAccs.length > 0 && surchargeAccs[0].location_name)
        || '';
      loadPlacement(resolvedName);
    } catch (err) {
      setError('Failed to load terminal data');
      console.error('Error loading terminal:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load related processor terminals for the same location
  const loadRelatedProcessorTerminals = async (locName) => {
    if (!locName) return;

    try {
      const response = await apiCall(`/api/v1/pai/account-mappings/by-location?location=${encodeURIComponent(locName)}`);
      const accounts = response.accounts || [];

      // Group by processor_type and terminal_number to get unique processor terminals
      const terminalMap = {};
      accounts.forEach(acc => {
        const key = `${acc.processor_type || 'PAI'}-${acc.terminal_number}`;
        if (!terminalMap[key]) {
          terminalMap[key] = {
            processorType: acc.processor_type || 'PAI',
            terminalNumber: acc.terminal_number,
            ghTerminalIds: acc.gh_terminal_ids || '',
            contractStatus: acc.contract_status,
            accountType: acc.account_type
          };
        }
        // Prefer vault account's GH terminal IDs
        if (acc.account_type === 'Vault' && acc.gh_terminal_ids) {
          terminalMap[key].ghTerminalIds = acc.gh_terminal_ids;
        }
      });

      setRelatedProcessorTerminals(Object.values(terminalMap));
    } catch (err) {
      console.error('Error loading related processor terminals:', err);
    }
  };

  // Add a new processor terminal for the same location
  const handleAddProcessorTerminal = async () => {
    if (!newProcessorTerminal.terminalNumber || !locationName) {
      setError('Terminal number and location name are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create a vault account for the new processor terminal with same GH mappings
      const payload = {
        terminalNumber: newProcessorTerminal.terminalNumber,
        locationName: locationName,
        ghTerminalIds: ghTerminalIds, // Copy GH terminals from current terminal
        nameOnAccount: vaultAccount?.name_on_account || 'PLACEHOLDER',
        route: vaultAccount?.route || '000000000',
        account: vaultAccount?.account || '00000',
        accountType: 'Vault',
        defaultFee: vaultAccount?.default_fee || '0.00',
        contractStatus: 'Active',
        settlementSchedule: 'Daily',
        processorType: newProcessorTerminal.processorType
      };

      await apiCall('/api/v1/pai/account-mappings', 'POST', payload);

      setSuccess(`${newProcessorTerminal.processorType} terminal ${newProcessorTerminal.terminalNumber} added successfully`);
      setIsAddingProcessorTerminal(false);
      setNewProcessorTerminal({ processorType: 'DNS', terminalNumber: '' });

      // Reload related terminals
      await loadRelatedProcessorTerminals(locationName);
    } catch (err) {
      setError('Failed to add processor terminal');
      console.error('Error adding processor terminal:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load frequent accounts and copy suggestions
  const loadQuickAddOptions = async () => {
    try {
      // Load frequent accounts
      const frequentResponse = await apiCall('/api/v1/pai/frequent-accounts');
      setFrequentAccounts(frequentResponse.accounts || []);
      
      // Load copy suggestions
      if (terminalId) {
        const suggestionsResponse = await apiCall('/api/v1/pai/copy-suggestions', 'GET', null, {
          terminal: terminalId,
          limit: 10
        });
        setExistingSuggestions(suggestionsResponse.existing || []);
      }
    } catch (err) {
      console.error('Error loading quick-add options:', err);
    }
  };

  // Load available locations for dropdown (PAI locations only)
  const loadLocations = async () => {
    try {
      // Use existing account-mappings endpoint and extract unique locations
      const response = await apiCall('/api/v1/pai/account-mappings');
      const accounts = response.accounts || [];
      // Extract unique locations from account mappings
      const uniqueLocations = [...new Set(
        accounts
          .map(acc => acc.location_name)
          .filter(loc => loc && loc !== '' && loc !== 'PLACEHOLDER')
      )].sort();
      setAvailableLocations(uniqueLocations);
    } catch (err) {
      console.error('Error loading locations:', err);
    }
  };

  // Load locations on component mount
  useEffect(() => {
    loadLocations();
  }, []);

  // Handle quick add from frequent accounts
  const handleQuickAddFrequent = (frequentAccount) => {
    setSurchargeForm({
      nameOnAccount: frequentAccount.name_on_account,
      route: frequentAccount.route,
      account: frequentAccount.account,
      splitAmount: ''
    });
    setIsAddingSurcharge(true);
    setValidationErrors({});
  };

  // Handle copy from existing account
  const handleCopyFromExisting = (index) => {
    if (index === '' || !existingSuggestions[index]) return;
    
    const suggestion = existingSuggestions[index];
    setSurchargeForm({
      nameOnAccount: suggestion.name_on_account,
      route: suggestion.route,
      account: suggestion.account,
      splitAmount: ''
    });
    setIsAddingSurcharge(true);
    setValidationErrors({});
  };

  // Load quick-add options and related processor terminals when terminal is loaded
  useEffect(() => {
    if (terminalLoaded) {
      loadQuickAddOptions();
      if (locationName) {
        loadRelatedProcessorTerminals(locationName);
      }
    }
  }, [terminalLoaded, locationName]);

  const validateVaultAccount = (data) => {
    const errors = {};
    
    if (!data.nameOnAccount) errors.nameOnAccount = 'Required';
    if (!data.route) errors.route = 'Required';
    else if (!/^\d{9}$/.test(data.route)) errors.route = 'Must be 9 digits';
    if (!data.account) errors.account = 'Required';
    else if (!/^\d{1,17}$/.test(data.account)) errors.account = '1-17 digits';
    if (!data.defaultFee) errors.defaultFee = 'Required';
    else if (isNaN(parseFloat(data.defaultFee))) errors.defaultFee = 'Must be a number';
    
    return errors;
  };

  const validateSurchargeAccount = (data) => {
    const errors = {};
    
    if (!data.nameOnAccount) errors.nameOnAccount = 'Required';
    if (!data.route) errors.route = 'Required';
    else if (!/^\d{9}$/.test(data.route)) errors.route = 'Must be 9 digits';
    if (!data.account) errors.account = 'Required';
    else if (!/^\d{1,17}$/.test(data.account)) errors.account = '1-17 digits';
    if (!data.splitAmount) errors.splitAmount = 'Required';
    else if (isNaN(parseFloat(data.splitAmount))) errors.splitAmount = 'Must be a number';
    
    return errors;
  };

  const handleSaveVault = async () => {
    const errors = validateVaultAccount(vaultForm);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const payload = {
        terminalNumber: terminalId,
        locationName: locationName || null,
        ...vaultForm,
        accountType: 'Vault',
        splitAmount: null,
        processorType: processorType
      };
      
      if (vaultAccount) {
        // Update existing
        const response = await apiCall(`/api/v1/pai/account-mappings/${vaultAccount.id}`, 'PUT', payload);
        setVaultAccount(response.account); // Update the vault account state with the response
      } else {
        // Create new
        const response = await apiCall('/api/v1/pai/account-mappings', 'POST', payload);
        setVaultAccount(response.account);
      }
      
      setIsEditingVault(false);
      setSuccess('Vault account saved successfully');
    } catch (err) {
      setError('Failed to save vault account');
      console.error('Error saving vault:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSurcharge = async () => {
    const errors = validateSurchargeAccount(surchargeForm);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Get the default fee from vault account or form
      const vaultFee = vaultAccount?.default_fee || vaultForm.defaultFee || '0.00';
      
      const payload = {
        terminalNumber: terminalId,
        locationName: locationName,
        ...surchargeForm,
        accountType: 'Surcharge',
        defaultFee: vaultFee, // Copy vault fee to each surcharge row
        processorType: processorType,
        contractStatus: surchargeForm.contractStatus,
        settlementSchedule: surchargeForm.settlementSchedule
      };
      
      if (editingSurchargeId) {
        // Update existing
        const response = await apiCall(`/api/v1/pai/account-mappings/${editingSurchargeId}`, 'PUT', payload);
        setSurchargeAccounts(surchargeAccounts.map(acc => 
          acc.id === editingSurchargeId ? response.account : acc
        ));
        setEditingSurchargeId(null);
      } else {
        // Create new
        const response = await apiCall('/api/v1/pai/account-mappings', 'POST', payload);
        setSurchargeAccounts([...surchargeAccounts, response.account]);
      }
      
      setIsAddingSurcharge(false);
      setSurchargeForm({
        nameOnAccount: '',
        route: '',
        account: '',
        splitAmount: '',
        contractStatus: 'Active',
        settlementSchedule: 'Daily'
      });
      setSuccess('Surcharge account saved successfully');
    } catch (err) {
      setError('Failed to save surcharge account');
      console.error('Error saving surcharge:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteDialog.account) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await apiCall(`/api/v1/pai/account-mappings/${deleteDialog.account.id}`, 'DELETE');
      
      if (deleteDialog.type === 'vault') {
        setVaultAccount(null);
        setVaultForm({
          nameOnAccount: '',
          route: '',
          account: '',
          defaultFee: '',
          contractStatus: 'Active',
          settlementSchedule: 'Daily'
        });
      } else {
        setSurchargeAccounts(surchargeAccounts.filter(acc => acc.id !== deleteDialog.account.id));
      }
      
      setDeleteDialog({ open: false, type: null, account: null });
      setSuccess('Account deleted successfully');
    } catch (err) {
      setError('Failed to delete account');
      console.error('Error deleting:', err);
    } finally {
      setLoading(false);
    }
  };

  // Delete entire terminal (all accounts for a terminal ID)
  const handleDeleteTerminal = async () => {
    if (!deleteTerminalDialog.terminalId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiCall(`/api/v1/pai/delete-terminal?terminal=${deleteTerminalDialog.terminalId}`, 'DELETE');

      setDeleteTerminalDialog({ open: false, terminalId: '' });
      setSuccess(`Terminal ${deleteTerminalDialog.terminalId} deleted successfully (${response.recordsDeleted} records removed)`);

      // Clear the current terminal state if we deleted the currently loaded terminal
      if (deleteTerminalDialog.terminalId.toUpperCase() === terminalId.toUpperCase()) {
        setTerminalId('');
        setTerminalLoaded(false);
        setVaultAccount(null);
        setSurchargeAccounts([]);
        setLocationName('');
        setGhTerminalIds('');
      }

      // Refresh related processor terminals
      if (locationName) {
        await loadRelatedProcessorTerminals(locationName);
      }
    } catch (err) {
      setError(err.message || 'Failed to delete terminal');
      console.error('Error deleting terminal:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSurcharge = (account) => {
    setEditingSurchargeId(account.id);
    setSurchargeForm({
      nameOnAccount: account.name_on_account,
      route: account.route,
      account: account.account,
      splitAmount: account.split_amount,
      contractStatus: account.contract_status || 'Active',
      settlementSchedule: account.settlement_schedule || 'Daily'
    });
    setIsAddingSurcharge(true);
  };
  
  // Quick save for inline editing (just status/schedule)
  const handleQuickSaveSurcharge = async (accountId) => {
    setLoading(true);
    setError(null);
    
    try {
      const account = surchargeAccounts.find(acc => acc.id === accountId);
      const updatedData = inlineEditSurcharge[accountId] || {};
      
      const payload = {
        terminalNumber: account.terminal_number || terminalId,
        locationName: account.location_name || locationName,
        nameOnAccount: account.name_on_account,
        route: account.route,
        account: account.account,
        splitAmount: account.split_amount,
        accountType: 'Surcharge',
        defaultFee: account.default_fee || vaultAccount?.default_fee || '0.00',
        contractStatus: updatedData.contract_status !== undefined ? updatedData.contract_status : account.contract_status,
        settlementSchedule: updatedData.settlement_schedule !== undefined ? updatedData.settlement_schedule : account.settlement_schedule
      };
      
      const response = await apiCall(`/api/v1/pai/account-mappings/${accountId}`, 'PUT', payload);
      
      // Update the surcharge accounts list with the response
      setSurchargeAccounts(surchargeAccounts.map(acc => 
        acc.id === accountId ? response.account : acc
      ));
      
      // Clear inline edit state
      setEditingInlineId(null);
      setInlineEditSurcharge(prev => {
        const newState = { ...prev };
        delete newState[accountId];
        return newState;
      });
      
      setSuccess('Surcharge account updated successfully');
    } catch (err) {
      setError('Failed to update surcharge account');
      console.error('Error updating surcharge:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Start inline editing
  const startInlineEdit = (account) => {
    setEditingInlineId(account.id);
    setInlineEditSurcharge({
      ...inlineEditSurcharge,
      [account.id]: {
        contract_status: account.contract_status || 'Active',
        settlement_schedule: account.settlement_schedule || 'Daily'
      }
    });
  };
  
  // Cancel inline editing
  const cancelInlineEdit = (accountId) => {
    setEditingInlineId(null);
    setInlineEditSurcharge(prev => {
      const newState = { ...prev };
      delete newState[accountId];
      return newState;
    });
  };

  const renderSplitCounter = () => {
    const { vaultFee, totalSplit } = calculateSplitTotals();
    const difference = vaultFee - totalSplit;
    const isValid = Math.abs(difference) < 0.01; // Allow for small rounding errors
    
    return (
      <Paper 
        sx={{ 
          p: 2, 
          mb: 2,
          backgroundColor: isValid ? 'success.light' : difference > 0 ? 'warning.light' : 'error.light',
          border: 1,
          borderColor: isValid ? 'success.main' : difference > 0 ? 'warning.main' : 'error.main'
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Box flex={1}>
            <Typography variant="h6" color="text.primary">
              Total Split: ${totalSplit.toFixed(2)} / ${vaultFee.toFixed(2)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isValid ? (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <CheckCircleIcon fontSize="small" color="success" />
                  Split amounts match vault default fee perfectly!
                </Box>
              ) : difference > 0 ? (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <WarningIcon fontSize="small" color="warning" />
                  Split amounts are short by ${Math.abs(difference).toFixed(2)}
                </Box>
              ) : (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <WarningIcon fontSize="small" color="error" />
                  Split amounts exceed vault default fee by ${Math.abs(difference).toFixed(2)}
                </Box>
              )}
            </Typography>
          </Box>
        </Box>
      </Paper>
    );
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom color="primary">
        PAI Account Mappings
      </Typography>
      
      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      
      {/* Location Selection Section */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)' }}>
        <CardContent>
          <Box display="flex" flexDirection="column" gap={2}>
            {/* Location Selection Row - Always visible */}
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="subtitle1" fontWeight="600" sx={{ minWidth: 120 }}>
                Location:
              </Typography>
              <select
                value={locationName}
                onChange={(e) => {
                  const newLocation = e.target.value;
                  setLocationName(newLocation);
                  if (newLocation) {
                    loadRelatedProcessorTerminals(newLocation);
                  } else {
                    setRelatedProcessorTerminals([]);
                  }
                  // Clear terminal when switching locations
                  setTerminalId('');
                  setTerminalLoaded(false);
                  setVaultAccount(null);
                  setSurchargeAccounts([]);
                  setGhTerminalIds('');
                }}
                style={{
                  height: '40px',
                  minWidth: '350px',
                  padding: '8px 12px',
                  fontSize: '14px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  backgroundColor: '#fff'
                }}
              >
                <option value="">-- Select a location --</option>
                {availableLocations.map((loc) => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
              {locationName && (
                <Chip
                  label={`${relatedProcessorTerminals.length} terminal(s) found`}
                  color="primary"
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>

            {/* Terminals for Selected Location */}
            {locationName && relatedProcessorTerminals.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Click a terminal to load it:
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {relatedProcessorTerminals.map((pt, index) => (
                    <Chip
                      key={`${pt.processorType}-${pt.terminalNumber}-${index}`}
                      label={`${pt.processorType}: ${pt.terminalNumber}`}
                      onClick={() => {
                        setProcessorType(pt.processorType);
                        setTerminalId(pt.terminalNumber);
                        // Auto-load the terminal
                        setTimeout(() => loadTerminal(false), 100);
                      }}
                      color={pt.terminalNumber === terminalId ? 'primary' : 'default'}
                      variant={pt.terminalNumber === terminalId ? 'filled' : 'outlined'}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {locationName && relatedProcessorTerminals.length === 0 && !loading && (
              <Alert severity="info" sx={{ mt: 1 }}>
                No terminals found for this location. Use the form below to add a new terminal.
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Terminal Section */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
        <CardContent>
          <Box display="flex" flexDirection="column" gap={2}>
            {/* Processor Selection Row */}
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="subtitle1" fontWeight="600" sx={{ minWidth: 120 }}>
                Processor:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 250 }}>
                <Select
                  value={processorType}
                  onChange={(e) => {
                    setProcessorType(e.target.value);
                    // If a terminal is already loaded, keep it so the user can reclassify
                    // its processor and Save (PUT updates processor_type in place). Only
                    // reset when starting a fresh lookup (nothing loaded yet).
                    if (!terminalLoaded) {
                      setTerminalId('');
                      setVaultAccount(null);
                      setSurchargeAccounts([]);
                      setGhTerminalIds('');
                    }
                  }}
                >
                  <MenuItem value="PAI">PAI (Payment Account Integration)</MenuItem>
                  <MenuItem value="SWITCH">Switch Commerce</MenuItem>
                  <MenuItem value="DNS">DNS (Digital Network Solutions)</MenuItem>
                </Select>
              </FormControl>
              <Chip
                label={
                  processorType === 'PAI' ? 'HG Terminal Format' :
                  processorType === 'DNS' ? 'DNS/NH Terminal Format' :
                  'Switch Terminal Format'
                }
                color="info"
                size="small"
                variant="outlined"
              />
            </Box>
            {/* Terminal ID Row */}
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="subtitle1" fontWeight="600" sx={{ minWidth: 120 }}>
                Terminal ID:
              </Typography>
              <TextField
                value={terminalId}
                onChange={(e) => setTerminalId(e.target.value.toUpperCase())}
                placeholder={
                  processorType === 'PAI' ? 'e.g., HG46479' :
                  processorType === 'DNS' ? 'e.g., DNS60547 or NH168833' :
                  'e.g., SC12345'
                }
                size="small"
                sx={{ width: 180 }}
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
              <Button
                variant="contained"
                onClick={() => loadTerminal(false)}
                disabled={loading || !terminalId}
                startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
              >
                {loading ? 'Loading...' : 'Load Terminal'}
              </Button>
              {terminalLoaded && (
                <>
                  <Chip
                    label={`✓ ${processorType} Terminal ${terminalId} loaded`}
                    color="success"
                    size="small"
                  />
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteTerminalDialog({ open: true, terminalId: terminalId })}
                  >
                    Delete Terminal
                  </Button>
                </>
              )}
            </Box>
            
            {/* GH Terminal IDs Row */}
            {terminalLoaded && (
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="subtitle1" fontWeight="600" sx={{ minWidth: 120 }}>
                  GH Terminal IDs:
                </Typography>
                <TextField
                  value={ghTerminalIds}
                  onChange={(e) => setGhTerminalIds(e.target.value)}
                  placeholder="Enter GH terminal IDs (comma-separated)"
                  size="small"
                  sx={{ width: 400 }}
                  disabled={!isEditingGhTerminals}
                  helperText="Enter GH terminal IDs separated by commas (e.g., GH001024, GH001025)"
                  InputProps={{
                    endAdornment: !isEditingGhTerminals && ghTerminalIds && (
                      <IconButton
                        size="small"
                        onClick={() => setIsEditingGhTerminals(true)}
                        title="Edit GH terminal IDs"
                      >
                        <EditIcon />
                      </IconButton>
                    )
                  }}
                />
              </Box>
            )}
            
            {/* Location Name Row - editable when adding/naming a location (no existing one) */}
            {terminalLoaded && (
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="subtitle1" fontWeight="600" sx={{ minWidth: 120 }}>
                  Location:
                </Typography>
                {isEditingLocation ? (
                  <TextField
                    size="small"
                    label="Location name"
                    placeholder="e.g., Rise Crystal River"
                    value={locationName}
                    onChange={(e) => setLocationName(e.target.value)}
                    sx={{ minWidth: 320 }}
                    autoFocus
                    helperText={!locationName ? 'Enter a name to enable Save All' : ' '}
                  />
                ) : (
                  <>
                    <Chip
                      label={locationName || 'Not set'}
                      color={locationName ? 'success' : 'default'}
                      variant="outlined"
                    />
                    <Button size="small" onClick={() => setIsEditingLocation(true)}>
                      {locationName ? 'Edit' : 'Set location'}
                    </Button>
                  </>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={saveAllChanges}
                  disabled={loading || !locationName}
                  startIcon={<SaveIcon />}
                  sx={{ ml: 2 }}
                >
                  Save All
                </Button>
              </Box>
            )}

            {/* Channel Assignment — places this location in an EXISTING Org -> Channel -> Market.
                Selectable only: new Orgs/Channels/Markets are created in the Channels module,
                not here. Cascading: choosing an Org filters Channels, choosing a Channel filters
                Markets. */}
            {terminalLoaded && (
              <Box display="flex" alignItems="center" gap={1.5} sx={{ mt: 2, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" fontWeight="600" sx={{ minWidth: 120 }}>
                  Channel:
                </Typography>
                <Autocomplete
                  size="small"
                  options={orgOptionList}
                  value={assignOrg || null}
                  onChange={(e, v) => { setAssignOrg(v || ''); setAssignChannel(''); setAssignMarket(''); }}
                  isOptionEqualToValue={(opt, val) => opt === val}
                  sx={{ minWidth: 200 }}
                  renderInput={(params) => <TextField {...params} label="Parent Org" placeholder="Select…" />}
                />
                <Autocomplete
                  size="small"
                  options={channelOptionList}
                  value={assignChannel || null}
                  onChange={(e, v) => { setAssignChannel(v || ''); setAssignMarket(''); }}
                  isOptionEqualToValue={(opt, val) => opt === val}
                  disabled={!assignOrg}
                  sx={{ minWidth: 200 }}
                  renderInput={(params) => <TextField {...params} label="Channel" placeholder="Select…" />}
                />
                <Autocomplete
                  size="small"
                  options={marketOptionList}
                  value={assignMarket || null}
                  onChange={(e, v) => { setAssignMarket(v || ''); }}
                  isOptionEqualToValue={(opt, val) => opt === val}
                  disabled={!assignChannel}
                  sx={{ minWidth: 160 }}
                  renderInput={(params) => <TextField {...params} label="Market" placeholder="Select…" />}
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  Saved with “Save All”. Create new Orgs/Channels/Markets in the Channels module.
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Vault Account Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <VaultIcon color="secondary" />
              <Typography variant="h6">Vault Account</Typography>
              <Chip label="Single Account Only" size="small" color="secondary" />
            </Box>
          </Box>
          <Divider sx={{ mb: 2 }} />
          
          {vaultAccount && !isEditingVault ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">NAME ON ACCOUNT</Typography>
                  <Typography variant="body2" fontWeight="500">{vaultAccount.name_on_account}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Typography variant="caption" color="text.secondary">ROUTE</Typography>
                  <Typography variant="body2" fontWeight="500">{vaultAccount.route}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">ACCOUNT</Typography>
                  <Typography variant="body2" fontWeight="500">****{vaultAccount.account.slice(-4)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Typography variant="caption" color="text.secondary">DEFAULT FEE</Typography>
                  <Typography variant="body2" fontWeight="500">${parseFloat(vaultAccount.default_fee).toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={1}>
                  <Typography variant="caption" color="text.secondary">STATUS</Typography>
                  <Chip 
                    label={vaultAccount.contract_status} 
                    size="small" 
                    color={vaultAccount.contract_status === 'Active' ? 'success' : 'default'}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={1}>
                  <Typography variant="caption" color="text.secondary">SCHEDULE</Typography>
                  <Typography variant="body2" fontWeight="500">{vaultAccount.settlement_schedule}</Typography>
                </Grid>
              </Grid>
              <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => {
                    setVaultForm({
                      nameOnAccount: vaultAccount.name_on_account,
                      route: vaultAccount.route,
                      account: vaultAccount.account,
                      defaultFee: vaultAccount.default_fee,
                      contractStatus: vaultAccount.contract_status,
                      settlementSchedule: vaultAccount.settlement_schedule
                    });
                    setIsEditingVault(true);
                  }}
                  title="Edit"
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setDeleteDialog({ open: true, type: 'vault', account: vaultAccount })}
                  title="Delete"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Paper>
          ) : (
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Name on Account *"
                    value={vaultForm.nameOnAccount}
                    onChange={(e) => setVaultForm({ ...vaultForm, nameOnAccount: e.target.value })}
                    error={!!validationErrors.nameOnAccount}
                    helperText={validationErrors.nameOnAccount}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Routing Number *"
                    value={vaultForm.route}
                    onChange={(e) => setVaultForm({ ...vaultForm, route: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                    error={!!validationErrors.route}
                    helperText={validationErrors.route}
                    placeholder="9 digits"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Account Number *"
                    value={vaultForm.account}
                    onChange={(e) => setVaultForm({ ...vaultForm, account: e.target.value.replace(/\D/g, '').slice(0, 17) })}
                    error={!!validationErrors.account}
                    helperText={validationErrors.account}
                    placeholder="Up to 17 digits"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Default Fee *"
                    value={vaultForm.defaultFee}
                    onChange={(e) => setVaultForm({ ...vaultForm, defaultFee: e.target.value })}
                    error={!!validationErrors.defaultFee}
                    helperText={validationErrors.defaultFee}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>
                    }}
                    type="number"
                    inputProps={{ step: 0.01, min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Contract Status</InputLabel>
                    <Select
                      value={vaultForm.contractStatus}
                      onChange={(e) => setVaultForm({ ...vaultForm, contractStatus: e.target.value })}
                      label="Contract Status"
                    >
                      <MenuItem value="Active">Active</MenuItem>
                      <MenuItem value="Inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Settlement</InputLabel>
                    <Select
                      value={vaultForm.settlementSchedule}
                      onChange={(e) => setVaultForm({ ...vaultForm, settlementSchedule: e.target.value })}
                      label="Settlement"
                    >
                      <MenuItem value="Daily">Daily</MenuItem>
                      <MenuItem value="Monthly">Monthly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
                <Button
                  size="small"
                  onClick={() => {
                    setIsEditingVault(false);
                    setValidationErrors({});
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveVault}
                  disabled={loading}
                >
                  Save Vault Account
                </Button>
              </Box>
            </Paper>
          )}
        </CardContent>
      </Card>
      
      {/* Surcharge Accounts Section */}
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <SurchargeIcon color="primary" />
              <Typography variant="h6">Surcharge Accounts</Typography>
            </Box>
          </Box>
          <Divider sx={{ mb: 2 }} />
          
          {/* Quick Add Section */}
          {terminalLoaded && !isAddingSurcharge && (
            <Paper sx={{ p: 2, mb: 2, backgroundColor: 'success.light', border: '1px solid', borderColor: 'success.main' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                <Typography variant="subtitle2" color="success.dark" fontWeight="600">
                  ⚡ Quick Add Options
                </Typography>
              </Box>
              <Box display="flex" gap={2} flexWrap="wrap">
                {/* Frequent Accounts Buttons */}
                {frequentAccounts.filter(acc => acc.is_active).map((account) => (
                  <Button
                    key={account.id}
                    variant="outlined"
                    color="success"
                    size="small"
                    startIcon={<StarIcon />}
                    onClick={() => handleQuickAddFrequent(account)}
                    sx={{ textTransform: 'none' }}
                  >
                    Add {account.display_name}
                  </Button>
                ))}
                
                {/* Copy from existing dropdown */}
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Copy from existing...</InputLabel>
                  <Select
                    value=""
                    onChange={(e) => handleCopyFromExisting(e.target.value)}
                    label="Copy from existing..."
                  >
                    <MenuItem value="">
                      <em>Select account to copy</em>
                    </MenuItem>
                    {existingSuggestions.map((suggestion, idx) => (
                      <MenuItem key={idx} value={idx}>
                        {suggestion.name_on_account} ({suggestion.route})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Paper>
          )}
          
          {/* Split Counter */}
          {(vaultAccount || vaultForm.defaultFee) && renderSplitCounter()}
          
          {/* Surcharge Accounts List */}
          {surchargeAccounts.map((account) => {
            const isInlineEditing = editingInlineId === account.id;
            const inlineData = inlineEditSurcharge[account.id] || {};
            
            return (
              <Paper key={account.id} variant="outlined" sx={{ p: 2, mb: 1 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={2}>
                    <Typography variant="caption" color="text.secondary">NAME ON ACCOUNT</Typography>
                    <Typography variant="body2" fontWeight="500">{account.name_on_account}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Typography variant="caption" color="text.secondary">ROUTE</Typography>
                    <Typography variant="body2" fontWeight="500">{account.route}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Typography variant="caption" color="text.secondary">ACCOUNT</Typography>
                    <Typography variant="body2" fontWeight="500">****{account.account.slice(-4)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={1}>
                    <Typography variant="caption" color="text.secondary">SPLIT</Typography>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      ${parseFloat(account.split_amount || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Typography variant="caption" color="text.secondary">STATUS</Typography>
                    {isInlineEditing ? (
                      <Select
                        size="small"
                        value={inlineData.contract_status || account.contract_status || 'Active'}
                        onChange={(e) => setInlineEditSurcharge({
                          ...inlineEditSurcharge,
                          [account.id]: {
                            ...inlineData,
                            contract_status: e.target.value
                          }
                        })}
                        sx={{ minWidth: 100 }}
                      >
                        <MenuItem value="Active">Active</MenuItem>
                        <MenuItem value="Inactive">Inactive</MenuItem>
                      </Select>
                    ) : (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: (account.contract_status || 'Active') === 'Active' ? 'success.main' : 'text.disabled',
                            fontWeight: 500
                          }}
                        >
                          {account.contract_status || 'Active'}
                        </Typography>
                        <IconButton 
                          size="small" 
                          onClick={() => startInlineEdit(account)}
                          sx={{ p: 0.25 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Typography variant="caption" color="text.secondary">SETTLEMENT</Typography>
                    {isInlineEditing ? (
                      <Select
                        size="small"
                        value={inlineData.settlement_schedule || account.settlement_schedule || 'Daily'}
                        onChange={(e) => setInlineEditSurcharge({
                          ...inlineEditSurcharge,
                          [account.id]: {
                            ...inlineData,
                            settlement_schedule: e.target.value
                          }
                        })}
                        sx={{ minWidth: 100 }}
                      >
                        <MenuItem value="Daily">Daily</MenuItem>
                        <MenuItem value="Monthly">Monthly</MenuItem>
                      </Select>
                    ) : (
                      <Typography variant="body2">{account.settlement_schedule || 'Daily'}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={12} md={1}>
                    <Box display="flex" gap={1}>
                      {isInlineEditing ? (
                        <>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleQuickSaveSurcharge(account.id)}
                            title="Save"
                            disabled={loading}
                          >
                            <SaveIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => cancelInlineEdit(account.id)}
                            title="Cancel"
                            disabled={loading}
                          >
                            <CancelIcon />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditSurcharge(account)}
                            title="Edit All Fields"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteDialog({ open: true, type: 'surcharge', account })}
                            title="Delete"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            );
          })}
          
          {/* Add Surcharge Form */}
          {isAddingSurcharge && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Name on Account *"
                    value={surchargeForm.nameOnAccount}
                    onChange={(e) => setSurchargeForm({ ...surchargeForm, nameOnAccount: e.target.value })}
                    error={!!validationErrors.nameOnAccount}
                    helperText={validationErrors.nameOnAccount}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Routing Number *"
                    value={surchargeForm.route}
                    onChange={(e) => setSurchargeForm({ ...surchargeForm, route: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                    error={!!validationErrors.route}
                    helperText={validationErrors.route}
                    placeholder="9 digits"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Account Number *"
                    value={surchargeForm.account}
                    onChange={(e) => setSurchargeForm({ ...surchargeForm, account: e.target.value.replace(/\D/g, '').slice(0, 17) })}
                    error={!!validationErrors.account}
                    helperText={validationErrors.account}
                    placeholder="Up to 17 digits"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Split Amount *"
                    value={surchargeForm.splitAmount}
                    onChange={(e) => setSurchargeForm({ ...surchargeForm, splitAmount: e.target.value })}
                    error={!!validationErrors.splitAmount}
                    helperText={validationErrors.splitAmount}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>
                    }}
                    type="number"
                    inputProps={{ step: 0.01, min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Contract Status</InputLabel>
                    <Select
                      value={surchargeForm.contractStatus}
                      onChange={(e) => setSurchargeForm({ ...surchargeForm, contractStatus: e.target.value })}
                      label="Contract Status"
                    >
                      <MenuItem value="Active">Active</MenuItem>
                      <MenuItem value="Inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Settlement</InputLabel>
                    <Select
                      value={surchargeForm.settlementSchedule}
                      onChange={(e) => setSurchargeForm({ ...surchargeForm, settlementSchedule: e.target.value })}
                      label="Settlement"
                    >
                      <MenuItem value="Daily">Daily</MenuItem>
                      <MenuItem value="Monthly">Monthly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={12} md={2}>
                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      onClick={() => {
                        setIsAddingSurcharge(false);
                        setEditingSurchargeId(null);
                        setSurchargeForm({
                          nameOnAccount: '',
                          route: '',
                          account: '',
                          splitAmount: '',
                          contractStatus: 'Active',
                          settlementSchedule: 'Daily'
                        });
                        setValidationErrors({});
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveSurcharge}
                      disabled={loading}
                    >
                      {editingSurchargeId ? 'Update' : 'Add'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          )}
          
          {/* Add Surcharge Button */}
          {!isAddingSurcharge && (
            <Box mt={2}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                  setIsAddingSurcharge(true);
                  setValidationErrors({});
                }}
                disabled={!terminalLoaded}
              >
                Add Surcharge Account
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, type: null, account: null })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this {deleteDialog.type} account?
          {deleteDialog.type === 'vault' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Deleting the vault account will affect split amount validation.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, type: null, account: null })}>
            Cancel
          </Button>
          <Button onClick={handleDeleteAccount} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Terminal Confirmation Dialog */}
      <Dialog open={deleteTerminalDialog.open} onClose={() => setDeleteTerminalDialog({ open: false, terminalId: '' })}>
        <DialogTitle>Delete Entire Terminal</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography>
            Are you sure you want to delete terminal <strong>{deleteTerminalDialog.terminalId}</strong> and ALL its associated account mappings?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will remove the vault account, all surcharge accounts, and any terminal mappings for this terminal ID.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTerminalDialog({ open: false, terminalId: '' })}>
            Cancel
          </Button>
          <Button onClick={handleDeleteTerminal} color="error" variant="contained" disabled={loading}>
            {loading ? 'Deleting...' : 'Delete Terminal'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bottom Save Button */}
      {terminalLoaded && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={saveAllChanges}
            disabled={loading || !locationName}
            startIcon={<SaveIcon />}
            sx={{ px: 4 }}
          >
            Save All Changes
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PaiAccountMappings;